import React from 'react';
import {
  Award,
  GraduationCap,
  UserCog,
  Stethoscope,
  Briefcase,
  ChefHat,
  Sparkles,
  ShieldCheck,
} from 'lucide-react';

const teams = [
  {
    icon: UserCog,
    title: 'Management Team',
    color: 'bg-[#1e3a5f]',
    iconColor: 'text-white',
    points: [
      'Chris Fletcher, Manager, overseeing the day-to-day running of Abney Low.',
      'A Deputy Manager supports the Manager and takes charge in their absence.',
      'Registered Nurses, Senior Carers and Carers deliver hands-on care to every resident.',
    ],
  },
  {
    icon: Stethoscope,
    title: 'Care Team',
    color: 'bg-[#c9a227]',
    iconColor: 'text-[#1e3a5f]',
    points: [
      'A team of 44 dedicated members of staff caring for our residents.',
      '8 staff hold an NVQ Level 3 in Health & Social Care.',
      '2 staff hold an NVQ Level 2, with another currently working towards it.',
    ],
  },
  {
    icon: Briefcase,
    title: 'Support Team',
    color: 'bg-emerald-600',
    iconColor: 'text-white',
    points: [
      'Admin & Head Office handle fees, residents\' personal allowances, supplier invoices and payroll.',
      'Domestic & Maintenance staff keep the home safe and spotless, in line with COSHH, HSE and IPC regulations.',
      'Our cooks work on alternate days, preparing three nutritious meals daily tailored to each resident\'s dietary needs.',
    ],
  },
];

const trainingTopics = [
  'Catheter Care',
  'Challenging Behaviour',
  'Malnutrition Universal Screening Tool (MUST)',
  'Dysphagia',
  'Common Health Problems',
  'End-of-Life Care',
  'Annual Dementia Refresher',
  'Safeguarding',
];

const TeamSection = () => {
  return (
    <section
      id="team"
      className="py-20 md:py-32 bg-white"
      data-testid="team-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Row */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1765896387387-0538bc9f997e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
                alt="A warm moment between one of our nurses and a resident at Abney Low"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </div>
            {/* Floating Badge */}
            <div className="absolute -bottom-6 -right-6 bg-[#c9a227] text-[#1e3a5f] p-6 rounded-2xl shadow-xl hidden lg:block">
              <Award className="w-8 h-8 mb-2" />
              <div className="text-lg font-bold">44+ Staff</div>
              <div className="text-sm">Experienced & Dedicated</div>
            </div>
          </div>

          {/* Content */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1e3a5f]/10 rounded-full text-[#1e3a5f] text-sm font-medium mb-6">
              Meet The Team
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6 leading-tight">
              A Dedicated and Experienced Team
            </h2>

            <p className="text-slate-600 text-lg mb-6 leading-relaxed">
              Abney Low is led by an experienced Registered Manager and
              supported by a multi-disciplinary team of qualified nurses,
              carers and support staff, all working together to deliver
              warm, personalised care.
            </p>

            <p className="text-slate-600 mb-8 leading-relaxed">
              Therapeutic and recreational activities, planned and run by
              our Activities Coordinator on a rolling four-weekly programme,
              are open to every resident, including those living with dementia.
            </p>

            {/* Training badges */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl">
                <GraduationCap className="w-6 h-6 text-[#c9a227]" />
                <div>
                  <div className="font-semibold text-[#1e3a5f]">Dementia Care</div>
                  <div className="text-sm text-slate-500">Specialist Training</div>
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl">
                <ShieldCheck className="w-6 h-6 text-[#c9a227]" />
                <div>
                  <div className="font-semibold text-[#1e3a5f]">Safeguarding</div>
                  <div className="text-sm text-slate-500">Certified</div>
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl">
                <Sparkles className="w-6 h-6 text-[#c9a227]" />
                <div>
                  <div className="font-semibold text-[#1e3a5f]">Clinical Best Practice</div>
                  <div className="text-sm text-slate-500">Ongoing Development</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Team Breakdown Cards */}
        <div className="mt-20">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f] mb-4">
              The People Behind the Care
            </h3>
            <p className="text-slate-600">
              From management through to the kitchen and maintenance team,
              every member of staff plays a vital role in making Abney Low
              feel like home.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {teams.map((team, index) => {
              const Icon = team.icon;
              return (
                <div
                  key={team.title}
                  className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg border border-slate-100 hover:border-[#c9a227]/40 transition-all"
                  data-testid={`team-card-${index}`}
                >
                  <div className={`w-14 h-14 rounded-xl ${team.color} flex items-center justify-center mb-5`}>
                    <Icon className={`w-7 h-7 ${team.iconColor}`} />
                  </div>
                  <h4 className="text-xl font-bold text-[#1e3a5f] mb-4">
                    {team.title}
                  </h4>
                  <ul className="space-y-3">
                    {team.points.map((point, i) => (
                      <li key={i} className="flex gap-3 text-slate-600 text-sm leading-relaxed">
                        <span className="text-[#c9a227] mt-1.5 flex-shrink-0">•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        {/* Training Block */}
        <div className="mt-12 bg-[#FDF6E3] rounded-3xl p-8 md:p-12">
          <div className="grid lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1e3a5f] text-white rounded-full text-xs font-medium mb-4">
                <GraduationCap className="w-3.5 h-3.5" />
                Continuous Training
              </div>
              <h3 className="text-2xl font-bold text-[#1e3a5f] mb-4 leading-tight">
                Investing in our team, every year
              </h3>
              <p className="text-slate-600 leading-relaxed text-sm">
                Beyond mandatory training, our team complete additional
                courses to expand their clinical and personal-care
                expertise. All staff also re-train annually in dementia
                care to keep their knowledge current.
              </p>
            </div>
            <div className="lg:col-span-2">
              <div className="grid sm:grid-cols-2 gap-3">
                {trainingTopics.map((topic) => (
                  <div
                    key={topic}
                    className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-100"
                  >
                    <ChefHat className="w-4 h-4 text-[#c9a227] flex-shrink-0 hidden" />
                    <div className="w-2 h-2 rounded-full bg-[#c9a227] flex-shrink-0" />
                    <span className="text-sm text-slate-700">{topic}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
